import { PRIVATEPATHS } from '@core/enums/paths.enum';
import { ISftpPartner } from '@core/models/sftp-transfer.model';
import { AnimatedListSkeleton, AnimatedTable, AnimatedTableBody, AnimatedTableRow } from 'components/animations';
import BreadCrumb from 'Common/BreadCrumb';
import ButtonBase from 'components/ui/buttons/ButtonBase';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import sftpIntegrationService from 'services/api/sftp-integration.service';
import SftpPartnerModal from './SftpPartnerModal';
import SftpPartnerWizard from './SftpPartnerWizard';
import {
    canPollPartner,
    canSendPartner,
    credentialBadge,
    directionBadgeClass,
    directionLabel,
} from './sftp-partner.utils';
import { connectionTestToastMessage, pollToastMessage, sendTestToastMessage } from './sftp-user-messages';
import SftpSetupGuide from './SftpSetupGuide';

function formatLinkedTo(p: ISftpPartner): string {
    const parts: string[] = [];
    if (p.supplierName) parts.push(`Supplier: ${p.supplierName}`);
    if (p.pharmacyDisplayName) parts.push(`Pharmacy: ${p.pharmacyDisplayName}`);
    return parts.length > 0 ? parts.join(' · ') : '—';
}

const SftpPartners: FC = () => {
    const [partners, setPartners] = useState<ISftpPartner[]>([]);
    const [loading, setLoading] = useState(false);
    const [showWizard, setShowWizard] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [editPartner, setEditPartner] = useState<ISftpPartner | null>(null);

    const loadPartners = useCallback(async () => {
        setLoading(true);
        try {
            const res = await sftpIntegrationService.listIntegrations();
            setPartners(res.data.integrations || []);
        } catch {
            /* interceptor */
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadPartners();
    }, [loadPartners]);

    const handleUpdate = async (values: Record<string, unknown>) => {
        if (!editPartner?.code) return;
        await sftpIntegrationService.updateIntegration(editPartner.code, values);
        toast.success('Partner updated');
        loadPartners();
    };

    const testConnection = async (code: string) => {
        try {
            const res = await sftpIntegrationService.testConnection(code);
            const result = res.data.connectionTest;
            const text = connectionTestToastMessage(result.message);
            if (result.success) toast.success(text);
            else toast.error(text);
            loadPartners();
        } catch {
            /* handled */
        }
    };

    const pollPartner = async (code: string) => {
        try {
            const res = await sftpIntegrationService.poll(code);
            toast.success(pollToastMessage(res.data?.message, code));
        } catch {
            /* handled */
        }
    };

    const sendTest = async (code: string) => {
        try {
            await sftpIntegrationService.sendTest(code);
            toast.success(sendTestToastMessage(null, code));
        } catch {
            /* handled */
        }
    };

    return (
        <>
            <BreadCrumb pageTitle="SFTP Partners" />
            <section className="py-5">
                <div className="admin-motion-card flex min-w-0 flex-col gap-2 bg-[#fff] p-3">
                    <div className="flex w-full flex-wrap items-center justify-between gap-3">
                        <h3 className="font-extrabold text-[#bd902e]">SFTP Partners</h3>
                        <div className="flex flex-wrap gap-2">
                            <Link
                                to={PRIVATEPATHS.SftpIntegrations}
                                className="inline-flex items-center rounded border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
                            >
                                Transfers
                            </Link>
                            <ButtonBase innerText="Add integration" onClick={() => setShowWizard(true)} />
                        </div>
                    </div>

                    <p className="text-sm text-slate-600">
                        Primary production SFTP partners may use server environment credentials (Track A). Add new partners with the wizard — passwords and keys are stored encrypted in the database.
                    </p>

                    <SftpSetupGuide />

                    {loading ? (
                        <AnimatedListSkeleton rows={4} />
                    ) : (
                        <div className="w-full min-w-0 overflow-x-auto">
                            <AnimatedTable className="table-custom w-full border-separate border-spacing-y-1 whitespace-nowrap">
                                <thead className="ltr:text-left rtl:text-right">
                                    <tr className="relative rounded-md bg-slate-50">
                                        <th className="px-3.5 py-2.5 font-semibold uppercase">Code</th>
                                        <th className="px-3.5 py-2.5 font-semibold uppercase">Direction</th>
                                        <th className="px-3.5 py-2.5 font-semibold uppercase">Linked to</th>
                                        <th className="px-3.5 py-2.5 font-semibold uppercase">Host</th>
                                        <th className="px-3.5 py-2.5 font-semibold uppercase">Enabled</th>
                                        <th className="px-3.5 py-2.5 font-semibold uppercase">Credentials</th>
                                        <th className="px-3.5 py-2.5 font-semibold uppercase">Actions</th>
                                    </tr>
                                </thead>
                                <AnimatedTableBody loading={loading} itemCount={partners.length} animationKey="sftp-partners">
                                    {partners.map((p) => {
                                        const badge = credentialBadge(p);
                                        return (
                                            <AnimatedTableRow
                                                key={p.code}
                                                rowKey={p.code}
                                                detailRecord={p}
                                                className="relative rounded-md bg-slate-50 hover:bg-[#bd902e10]"
                                            >
                                                <td className="px-3.5 py-2.5">
                                                    <span className="font-semibold text-slate-800">{p.code}</span>
                                                    <br />
                                                    <span className="text-xs text-slate-500">{p.name}</span>
                                                </td>
                                                <td className="px-3.5 py-2.5">
                                                    <span className={`inline-flex rounded px-2 py-0.5 text-xs font-medium ${directionBadgeClass(p.direction)}`}>
                                                        {directionLabel(p.direction)}
                                                    </span>
                                                </td>
                                                <td className="max-w-[220px] px-3.5 py-2.5 text-sm text-slate-700">{formatLinkedTo(p)}</td>
                                                <td className="px-3.5 py-2.5">{p.host}:{p.port}</td>
                                                <td className="px-3.5 py-2.5">{p.enabled ? 'Yes' : 'No'}</td>
                                                <td className="px-3.5 py-2.5">
                                                    <span className={`inline-flex rounded px-2 py-0.5 text-xs font-medium ${badge.className}`}>
                                                        {badge.label}
                                                    </span>
                                                </td>
                                                <td className="px-3.5 py-2.5">
                                                    <div className="flex flex-wrap gap-2">
                                                        <button type="button" className="text-sm font-semibold text-[#bd902e] hover:underline" onClick={() => { setEditPartner(p); setShowModal(true); }}>
                                                            Edit
                                                        </button>
                                                        <button type="button" className="text-sm font-semibold text-slate-600 hover:underline" onClick={() => testConnection(p.code!)}>
                                                            Test
                                                        </button>
                                                        {canPollPartner(p.direction) && (
                                                            <button type="button" className="text-sm font-semibold text-slate-600 hover:underline" onClick={() => pollPartner(p.code!)}>
                                                                Download files
                                                            </button>
                                                        )}
                                                        {canSendPartner(p.direction) && (
                                                            <button type="button" className="text-sm font-semibold text-slate-600 hover:underline" onClick={() => sendTest(p.code!)}>
                                                                Send test
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </AnimatedTableRow>
                                        );
                                    })}
                                </AnimatedTableBody>
                            </AnimatedTable>
                        </div>
                    )}
                </div>
            </section>
            <SftpPartnerWizard show={showWizard} handleClose={() => setShowWizard(false)} onComplete={loadPartners} />
            <SftpPartnerModal
                show={showModal}
                handleClose={() => { setShowModal(false); setEditPartner(null); }}
                onSubmit={handleUpdate}
                initial={editPartner}
                isEdit
            />
        </>
    );
};

export default SftpPartners;
